import { translateBackend } from '@assets/i18n/config';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { Alert, Box, Button, Grid, Typography } from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { CalculatorDisclaimer } from './components/calculatorDisclaimer';
import { CalculatorInputPanel } from './components/calculatorInputPanel';
import { CalculatorPdfCaptureClone } from './components/calculatorPdfCaptureClone';
import { CalculatorPdfPreviewDialog } from './components/calculatorPdfPreviewDialog';
import { CalculatorResultsPanel } from './components/calculatorResultsPanel';
import type { CalculatorState } from './useCalculator';
import { useCalculatorPdf } from './useCalculatorPdf';

export const CalculatorVisual = ({
  calculator,
  onContinue,
  options,
}: {
  calculator: CalculatorState;
  options: Record<string, unknown>;
  onContinue?: () => void;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { integrationResult, mutationError } = calculator;
  const showContinueButton = !options.hideContinueButton;
  const showDownloadButton = !!options.enableDownload;
  const titleAdditionalInfo = additionalInfo(
    (options.title as { additionalInfo?: unknown } | undefined)?.additionalInfo,
  );
  const {
    captureSnapshot,
    closePreview,
    download,
    error,
    filename,
    isGenerating,
    isSaving,
    pdfInputRef,
    pdfResultsRef,
    previewOpen,
    previewSections,
    savePdf,
    setFilename,
  } = useCalculatorPdf({ calculator, title: t('calculator.title') });

  return (
    <Box data-testid='calculator-visual'>
      <Typography variant='headingMedium' sx={{ fontWeight: 500, mb: 3 }}>
        {translateBackend(options.title) || t('calculator.title')}
        {titleAdditionalInfo && (
          <InfoDialog information={titleAdditionalInfo} sx={{ fontSize: 24 }} />
        )}
      </Typography>
      {mutationError && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Alert severity='error'>{mutationError}</Alert>
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Button
              variant='text'
              startIcon={<Refresh />}
              onClick={calculator.handleCalculate}
              disabled={calculator.isCalculating || calculator.accountLoading}
              data-testid='calculator-retry-button'
            >
              {t('shared:retry')}
            </Button>
          </Box>
        </Box>
      )}
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CalculatorInputPanel calculator={calculator} options={options} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <CalculatorResultsPanel calculator={calculator} options={options} />
          {showDownloadButton && (
            <Box sx={{ mt: 2 }}>
              {error === 'generate' && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {t('calculator.pdfPreview.generateError')}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  disabled={
                    integrationResult?.status !== 'success' || isGenerating
                  }
                  loading={isGenerating}
                  onClick={download}
                  data-testid='calculator-download-button'
                >
                  {t('calculator.download')}
                </Button>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      {!!options.showDisclaimer && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <CalculatorDisclaimer options={options} />
        </Box>
      )}
      {showContinueButton && onContinue && (
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button onClick={onContinue} data-testid='calculator-continue-button'>
            {t('continue')}
          </Button>
        </Box>
      )}
      {captureSnapshot && (
        <CalculatorPdfCaptureClone
          calculator={captureSnapshot}
          inputRef={pdfInputRef}
          options={options}
          resultsRef={pdfResultsRef}
        />
      )}
      <CalculatorPdfPreviewDialog
        error={error === 'save'}
        filename={filename}
        isSaving={isSaving}
        onClose={closePreview}
        onFilenameChange={setFilename}
        onSave={savePdf}
        open={previewOpen}
        sections={previewSections}
      />
    </Box>
  );
};
